import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import ArrowRight from '@mui/icons-material/ArrowRight';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/GridLegacy';
import Grow from '@mui/material/Grow';
import ListSubheader from '@mui/material/ListSubheader';
import MUIMenu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import classnames from 'classnames';
import type { PropsWithChildren, MouseEvent, FC } from 'react';
import React, { useState, forwardRef, Children, Fragment } from 'react';

import { withStyles } from '../../styles/tools';
import { withTrace } from '../../utils';
import { Spinner } from '../Spinner';
import { styles } from './Menu.styles';
import type {
    MenuComponentProps,
    MenuItemConfig,
    MenuItemWithIconComponentProps,
} from './Menu.types';

export const MenuItemWithIconBase = ({
    IconElement,
    label,
    loading = false,
    applyTrace,
}: MenuItemWithIconComponentProps) => (
    <Grid container wrap="nowrap" alignItems="center" spacing={2} {...applyTrace()}>
        <Grid item {...applyTrace('icon')}>
            {loading ? (
                <Spinner size={24} position="relative" color="inherit" {...applyTrace('loading')} />
            ) : (
                <IconElement />
            )}
        </Grid>
        <Grid item {...applyTrace('label')}>
            {label}
        </Grid>
    </Grid>
);

export const MenuItemWithIcon = withTrace('item')(MenuItemWithIconBase);

export function MenuBase(props: PropsWithChildren<MenuComponentProps>) {
    const {
        open,
        options,
        optionGroups,
        classes,
        onClose,
        popoverClasses,
        applyTrace,
        customHeight,
        ...menuProps
    } = props;

    const optionGroupsCollapsedDefault: Record<string, boolean> = {};
    if (optionGroups !== undefined) {
        optionGroups.forEach((optionGroup) => {
            if (optionGroup.key !== undefined) {
                optionGroupsCollapsedDefault[optionGroup.key] =
                    optionGroup.collapsedDefault === true;
            }
        });
    }
    // From optionGroup key to whether it is collapsed
    const [optionGroupsCollapsed, setOptionGroupsCollapsed] = useState<Record<string, boolean>>(
        optionGroupsCollapsedDefault,
    );

    return (
        <MUIMenu
            open={open}
            classes={{ paper: classes.paper }}
            PopoverClasses={popoverClasses}
            MenuListProps={{ disablePadding: true }}
            onClick={(event: MouseEvent) => {
                onClose();
                event.stopPropagation();
            }}
            onClose={(event: MouseEvent<HTMLElement>) => {
                onClose();
                event.stopPropagation();
            }}
            {...menuProps}
            disableScrollLock
            sx={{
                '& .MuiPaper-root': {
                    maxHeight: customHeight ?? 'auto',
                },
            }}
            {...applyTrace('mui-menu')}
        >
            {optionGroups ? (
                optionGroups.map((optGr, index) => (
                    <span key={index} className={classes.menuGroupWrapper}>
                        {index > 0 ? (
                            <Divider variant="middle" className={classes.divider} />
                        ) : null}
                        {optGr.collapsible ? (
                            <Grid
                                automation-id={`option-group-header-${optGr.key ?? optGr.label}`}
                                container
                                direction="row"
                                style={{ cursor: 'pointer' }}
                                onClick={(event) => {
                                    const nextOptionGroupsCollapsed = { ...optionGroupsCollapsed };
                                    if (optGr.key !== undefined) {
                                        nextOptionGroupsCollapsed[optGr.key] =
                                            !nextOptionGroupsCollapsed[optGr.key];
                                    }
                                    setOptionGroupsCollapsed(nextOptionGroupsCollapsed);
                                    event.stopPropagation();
                                }}
                            >
                                <Grid>
                                    <ListSubheader className={classes.menuLabel}>
                                        {optGr.label}
                                    </ListSubheader>
                                </Grid>
                                <Grid item>
                                    {optGr.key !== undefined && optionGroupsCollapsed[optGr.key] ? (
                                        <ArrowRight
                                            className={classes.collapsibleOptionGroupArrow}
                                        />
                                    ) : (
                                        <ArrowDropDown
                                            className={classes.collapsibleOptionGroupArrow}
                                        />
                                    )}
                                </Grid>
                            </Grid>
                        ) : (
                            <ListSubheader className={classes.menuLabel}>
                                {optGr.label}
                            </ListSubheader>
                        )}
                        {(!optGr.collapsible ||
                            optGr.key === undefined ||
                            !optionGroupsCollapsed[optGr.key]) &&
                            optGr.optionIds.map((optKey) =>
                                options
                                    .filter((opt) => opt.key === optKey)
                                    .map((option: MenuItemConfig, index) => (
                                        <MenuOptionsList
                                            key={index}
                                            {...{ ...props, options: [option] }}
                                        />
                                    )),
                            )}
                    </span>
                ))
            ) : (
                <MenuOptionsList {...props} />
            )}
        </MUIMenu>
    );
}
export const MenuOptionsList = forwardRef<HTMLLIElement, MenuComponentProps>((props, ref) => {
    const { options, onClose, classes, applyTrace, children, actionButton } = props;
    return (
        // MenuItem must be a direct child of Menu for arrow key controls to work.
        // Fragments ensure that.
        <>
            {actionButton}
            {options.map(
                ({
                    key,
                    action,
                    isDisabled = () => false,
                    ItemComponent,
                    selected,
                    itemProps,
                    divideAfter = false,
                    dividerClassName,
                }: MenuItemConfig) => (
                    <Fragment key={key}>
                        <MenuItem
                            ref={ref}
                            className={classes.menuItem}
                            disabled={isDisabled()}
                            selected={selected}
                            onClick={(event: MouseEvent) => {
                                onClose();
                                if (action) {
                                    action(key);
                                }
                                event.stopPropagation();
                            }}
                            {...applyTrace(`menu-item-${key}`)}
                        >
                            <ItemComponent {...itemProps} />
                        </MenuItem>
                        {divideAfter ? (
                            <Divider
                                className={dividerClassName}
                                {...applyTrace(`divider-after-${key}`)}
                            />
                        ) : null}
                    </Fragment>
                ),
            )}
            {Children.map(children, (child, index) => (
                <span key={index}>{child}</span> // Wrapping in span solves forward ref problems.
            ))}
        </>
    );
});

export function AttachedMenuBase(props: PropsWithChildren<MenuComponentProps>) {
    const {
        open,
        classes,
        onClose,
        popoverClasses,
        applyTrace,
        anchorEl,
        popperClassName,
        popperPlacement = 'bottom-start',
        ...menuProps
    } = props;

    const paperClasses =
        popoverClasses && popoverClasses.paper
            ? classnames(classes.paper, popoverClasses.paper)
            : classes.paper;

    // oddness, the styles on the Popper are being removed before the component is unmounted
    // add an extra style "display: none" if anchorEl is null
    const popper = classnames(popperClassName, { [classes.popper]: !anchorEl });

    return (
        <Popper
            className={popper}
            anchorEl={anchorEl}
            open={open}
            placement={popperPlacement}
            modifiers={[
                {
                    name: 'flip',
                    enabled: false,
                },
                {
                    name: 'offset',
                    enabled: true,
                    options: {
                        offset: [0, 0],
                    },
                },
                {
                    name: 'preventOverflow',
                    enabled: true,
                    options: {
                        escapeWithReference: true,
                    },
                },
            ]}
            transition
            disablePortal
        >
            {({ TransitionProps }) => (
                <ClickAwayListener
                    mouseEvent="onMouseUp"
                    touchEvent="onTouchEnd"
                    onClickAway={(event: any) => {
                        if (anchorEl && anchorEl.contains(event.target)) {
                            return;
                        }

                        if (event.currentTarget !== anchorEl) {
                            onClose();
                            event.stopPropagation();
                        }
                    }}
                >
                    <Grow {...TransitionProps}>
                        <Paper className={paperClasses}>
                            <MenuList disablePadding {...menuProps} {...applyTrace('mui-menu')}>
                                <MenuOptionsList {...props} />
                            </MenuList>
                        </Paper>
                    </Grow>
                </ClickAwayListener>
            )}
        </Popper>
    );
}
const traced: FC<PropsWithChildren<Omit<MenuComponentProps, 'applyTrace'>>> =
    withTrace('menu')(MenuBase);
const attachedTraced = withTrace('menu')(AttachedMenuBase);

export const Menu = withStyles(styles)(traced);
export const AttachedMenu = withStyles(styles)(attachedTraced);
export default Menu;
